import { HelpCircle, LogOut } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useVendorAuth } from '@/auth/VendorAuthProvider'
import { useBrand } from '@/brand/BrandProvider'
import { useTour } from '@/tour/TourProvider'
import { Avatar, BrandLogo } from './ui'

// Ninguna guía cubre esta pantalla (no es ni el home del vendedor ni el del admin, y sus
// pasos navegarían a rutas que el guard de "tenant en provisioning" rebota de vuelta acá).
// Se deshabilita el botón en vez de intentar armarle una guía propia a una pantalla de
// paso único.
const NO_TOUR_ROUTES = ['/admin/conexion']

export function Header() {
  const { vendor, logout } = useVendorAuth()
  const brand = useBrand()
  const { start } = useTour()
  const location = useLocation()
  const tourDisabled = NO_TOUR_ROUTES.includes(location.pathname)
  const [menuOpen, setMenuOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setMenuOpen(false)
    }
    document.addEventListener('mousedown', onClickOutside)
    return () => document.removeEventListener('mousedown', onClickOutside)
  }, [])

  return (
    <header className="sticky top-0 z-20 flex items-center justify-between gap-3 border-b border-line bg-surface/95 px-4 py-3 backdrop-blur">
      <BrandLogo logoUrl={brand.logoUrl} appName={brand.appName} />

      <div className="flex items-center gap-1">
        <button
          type="button"
          data-tour="help"
          onClick={start}
          disabled={tourDisabled}
          aria-label="Ver guía de la app"
          title={tourDisabled ? 'La guía no está disponible en esta pantalla' : undefined}
          className="rounded-full p-2 text-ink-muted hover:bg-surface-sunken disabled:opacity-30 disabled:hover:bg-transparent"
        >
          <HelpCircle className="h-5 w-5" />
        </button>

        {vendor && (
        <div className="relative" ref={ref}>
          <button
            className="flex items-center gap-2"
            onClick={() => setMenuOpen((o) => !o)}
            aria-label="Perfil"
          >
            <div className="text-right leading-tight">
              <p className="max-w-[9rem] truncate text-sm font-semibold text-ink">{vendor.fullName}</p>
              {vendor.vendorNumber && (
                <p className="text-[11px] text-ink-muted">V-{vendor.vendorNumber}</p>
              )}
            </div>
            <Avatar name={vendor.fullName} />
          </button>

          {menuOpen && (
            <div className="absolute right-0 top-full z-30 mt-2 w-56 overflow-hidden rounded-xl border border-line bg-surface shadow-pop">
              <div className="border-b border-line px-4 py-3">
                <p className="truncate text-sm font-semibold text-ink">{vendor.fullName}</p>
                <p className="truncate text-xs text-ink-muted">{vendor.email}</p>
                <p className="mt-1 text-xs text-ink-faint">{vendor.company} · {vendor.branch}</p>
              </div>
              <button
                className="flex w-full items-center gap-2 px-4 py-3 text-sm font-medium text-danger hover:bg-surface-sunken"
                onClick={logout}
              >
                <LogOut className="h-4 w-4" />
                Cerrar sesión
              </button>
            </div>
          )}
        </div>
      )}
      </div>
    </header>
  )
}
